import React, { useEffect, useState } from 'react'
import { addDays, format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

const today = () => format(new Date(), 'yyyy-MM-dd')
const plusWeeks = (date, weeks) => format(addDays(new Date(`${date}T00:00:00`), weeks * 7), 'yyyy-MM-dd')

/**
 * Start (mode "start") or edit (mode "edit") a sprint: name, dates, goal.
 * onSubmit({ name, goal, start_date, end_date }) — returns a promise; the dialog closes on success.
 */
export const SprintFormDialog = ({ open, mode, sprint, onOpenChange, onSubmit }) => {
  const [values, setValues] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !sprint) return
    const start = sprint.start_date || today()
    setValues({
      name: sprint.name || '',
      goal: sprint.goal || '',
      start_date: start,
      end_date: sprint.end_date || plusWeeks(start, 2),
      weeks: sprint.end_date ? 'custom' : '2'
    })
  }, [open, sprint])

  const setDuration = (weeks) => setValues(v => ({ ...v, weeks, ...(weeks !== 'custom' && { end_date: plusWeeks(v.start_date, Number(weeks)) }) }))

  const submit = async () => {
    setSaving(true)
    try {
      await onSubmit({ name: values.name, goal: values.goal, start_date: values.start_date || null, end_date: values.end_date || null })
      onOpenChange(false)
    } catch {
      // the caller already showed the error; keep the dialog open to fix it
    } finally {
      setSaving(false)
    }
  }

  const starting = mode === 'start'
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{starting ? `Start ${sprint?.name || 'sprint'}` : 'Edit sprint'}</DialogTitle>
          {starting && (
            <DialogDescription>
              {sprint?.stats?.issues || 0} {sprint?.stats?.issues === 1 ? 'issue' : 'issues'} · {sprint?.stats?.points || 0} points will be included.
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="sprint-name">Sprint name</Label>
            <Input id="sprint-name" value={values.name || ''} maxLength={100} onChange={(e) => setValues(v => ({ ...v, name: e.target.value }))} />
          </div>
          <div className="grid grid-cols-[7rem_1fr_1fr] gap-2">
            <div className="space-y-1.5">
              <Label>Duration</Label>
              <Select value={values.weeks || '2'} onValueChange={setDuration}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['1', '2', '3', '4'].map(w => <SelectItem key={w} value={w}>{w} week{w === '1' ? '' : 's'}</SelectItem>)}
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sprint-start">Start</Label>
              <Input id="sprint-start" type="date" value={values.start_date || ''}
                onChange={(e) => setValues(v => ({ ...v, start_date: e.target.value, ...(v.weeks !== 'custom' && e.target.value && { end_date: plusWeeks(e.target.value, Number(v.weeks)) }) }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sprint-end">End</Label>
              <Input id="sprint-end" type="date" value={values.end_date || ''} onChange={(e) => setValues(v => ({ ...v, end_date: e.target.value, weeks: 'custom' }))} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sprint-goal">Sprint goal</Label>
            <Textarea id="sprint-goal" rows={3} value={values.goal || ''} onChange={(e) => setValues(v => ({ ...v, goal: e.target.value }))} placeholder="What should this sprint achieve?" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !values.name?.trim()}>{starting ? 'Start sprint' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Complete the active sprint; unfinished work goes to the backlog, a planned sprint or a new one. */
export const CompleteSprintDialog = ({ open, sprint, plannedSprints, onOpenChange, onSubmit }) => {
  const [moveTo, setMoveTo] = useState('backlog')
  const [saving, setSaving] = useState(false)
  useEffect(() => { if (open) setMoveTo('backlog') }, [open])

  const stats = sprint?.stats || {}
  const unfinished = (stats.issues || 0) - (stats.done_issues || 0)

  const submit = async () => {
    setSaving(true)
    try {
      await onSubmit(moveTo === 'backlog' || moveTo === 'new' ? moveTo : Number(moveTo))
      onOpenChange(false)
    } catch {
      // the caller already showed the error
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Complete {sprint?.name}</DialogTitle>
          <DialogDescription>
            {stats.done_issues || 0} {stats.done_issues === 1 ? 'issue is' : 'issues are'} done ({stats.done_points || 0} points)
            {unfinished > 0 ? ` and ${unfinished} ${unfinished === 1 ? 'is' : 'are'} unfinished.` : '. Everything is done!'}
          </DialogDescription>
        </DialogHeader>
        {unfinished > 0 && (
          <div className="space-y-1.5">
            <Label>Move unfinished issues to</Label>
            <Select value={String(moveTo)} onValueChange={setMoveTo}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="backlog">Backlog</SelectItem>
                <SelectItem value="new">A new sprint</SelectItem>
                {plannedSprints.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Their sub-tasks move with them. Done work stays in this sprint's record.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>Complete sprint</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
